import { Controller } from "@hotwired/stimulus";

// Re-mint the reCAPTCHA v3 token as the form is submitted.
//
// The gem's inline script calls grecaptcha.execute once, on page load, and
// nothing ever refreshes the token it writes into the hidden field. Google's v3
// tokens are valid for two minutes. An applicant who takes longer than that —
// the ordinary case on a form asking for six fields, and certain on the step-7
// review — submits an expired token, verify_recaptcha fails, and
// ApplySpamGuards#spam_redirect shows them the same "Application received."
// page a real submission shows. Nobody, applicant or owner, ever learns.
//
// Minting here instead means the token is always seconds old, and it is scored
// on the whole form-filling session rather than on an empty page 100ms after
// load.
//
// If minting fails — grecaptcha blocked by an extension, network down, Google
// slow — the form is submitted anyway carrying whatever token it already had.
// That is exactly what happens today, so this can only improve the outcome and
// never blocks someone who would otherwise have got through.
export default class extends Controller {
  static values = {
    siteKey: String,
    action: String,
    timeout: { type: Number, default: 5000 },
  };

  connect() {
    this.passthrough = false;
    this.minting = false;
  }

  async refresh(event) {
    // Our own re-submit below; let it through untouched.
    if (this.passthrough) return;

    const field = this.tokenField();
    if (!field) return; // a wizard step that renders no reCAPTCHA field

    // Back skips every spam gate server-side, so it needs no token.
    if (event.submitter && event.submitter.value === "back") return;

    event.preventDefault();

    // A second click while the first mint is still in flight: swallow it rather
    // than letting it through, which would submit the stale token we are here
    // to replace.
    if (this.minting) return;
    this.minting = true;

    try {
      const token = await this.mintToken();
      if (token) field.value = token;

      // Pass the submitter through: the wizard's buttons carry wizard_direction,
      // and dropping it would silently turn a submit into an advance_step.
      this.passthrough = true;
      this.element.requestSubmit(event.submitter);
      this.passthrough = false;
    } finally {
      this.minting = false;
    }
  }

  tokenField() {
    return this.element.querySelector(
      'input[name^="g-recaptcha-response-data"]',
    );
  }

  // Resolves with a fresh token, or null if grecaptcha is unavailable, errors,
  // or takes longer than timeoutValue.
  mintToken() {
    const grecaptcha = window.grecaptcha;
    if (!grecaptcha) return Promise.resolve(null);

    const mint = new Promise((resolve) => {
      grecaptcha.ready(() => {
        grecaptcha
          .execute(this.siteKeyValue, { action: this.actionValue })
          .then(resolve)
          .catch(() => resolve(null));
      });
    });

    const expiry = new Promise((resolve) => {
      setTimeout(() => resolve(null), this.timeoutValue);
    });

    return Promise.race([mint, expiry]);
  }
}
